import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { EntitlementService } from '../../core/tenancy/entitlement.service';
import { TenancyService } from '../../core/tenancy/tenancy.service';
import { NavigationService } from '../../core/navigation/navigation.service';
import { PendingRequestsService } from '../../core/notifications/pending-requests.service';
import { ConnectivityService } from '../../core/connectivity/connectivity.service';
import { OfflineQueueService } from '../../core/offline/offline-queue.service';
import { ApprovalsStore } from '../../core/stores/approvals.store';
import { FloorStore } from '../../core/stores/floor.store';
import { OrdersStore } from '../../core/stores/orders.store';
import { NvIcon } from '../../shared/ui/icon/nv-icon';
import { NvTooltip } from '../../shared/ui/misc/nv-tooltip';
import { NvVenueSwitcher } from '../venue-switcher/nv-venue-switcher';

/** NexVenueX's own support inbox — no support contact exists in environment/config, so this is the one place it's set. */
export const SUPPORT_MAILTO = 'mailto:support@nexvenuex.com';

/**
 * The light side rail — console redesign (Cafooze POS prototype `<aside>`).
 * 236px open / 62px collapsed, venue tile, "Daily work" / "Manage" groups,
 * amber count badges, and a footer with Settings, Help & support and a
 * save-state indicator driven by the offline queue.
 *
 * Purely presentational: every item's visibility is decided by
 * NavigationService. This component only lays the result out and adds the
 * badge counts, each read from a store that is already live app-wide.
 */
@Component({
  selector: 'nv-rail',
  imports: [RouterLink, RouterLinkActive, NvIcon, NvTooltip, NvVenueSwitcher],
  templateUrl: './nv-rail.html',
  styleUrl: './nv-rail.scss',
  host: { '[attr.data-collapsed]': 'collapsed()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NvRail {
  private readonly nav = inject(NavigationService);
  private readonly entitlements = inject(EntitlementService);
  private readonly tenancy = inject(TenancyService);
  private readonly requests = inject(PendingRequestsService);
  private readonly floor = inject(FloorStore);
  private readonly ordersStore = inject(OrdersStore);
  private readonly approvalsStore = inject(ApprovalsStore);
  private readonly connectivity = inject(ConnectivityService);
  private readonly offlineQueue = inject(OfflineQueueService);

  readonly collapsed = input(false);

  protected readonly groups = this.nav.groups;
  protected readonly footerItems = this.nav.footerItems;
  protected readonly supportHref = SUPPORT_MAILTO;

  protected readonly venueName = this.entitlements.venueName;
  protected readonly logoUrl = this.entitlements.logoUrl;

  /** Second line under the venue name — there is no branch field, so the plan (or the tagline) stands in. */
  protected readonly venueContext = computed(() => {
    if (this.tenancy.isRoot()) return 'Platform admin';
    const plan = this.entitlements.planName();
    if (plan) return `${plan.charAt(0).toUpperCase()}${plan.slice(1)} plan`;
    return this.entitlements.tagline() ?? '';
  });

  /** Tables = tables needing cleaning + open guest requests. */
  private readonly tablesCount = computed(() => this.floor.stateCounts().cleaning + this.requests.count());

  /** Billing = tables in the 'bill' state (a draft bill, or the guest asked for it). */
  private readonly billingCount = computed(() => this.floor.stateCounts().bill);

  /** Orders = orders waiting for staff approval. */
  private readonly ordersCount = computed(() => this.ordersStore.pendingApproval().length);

  /** Tasks = pending approvals only (prototype), for the roles that can act on them (same gate as TasksHub's Approvals tab). Open tasks don't badge. */
  private readonly tasksCount = computed(() => {
    const canApprove = this.tenancy.isAdmin() || this.tenancy.role() === 'manager';
    return canApprove ? this.approvalsStore.pendingCount() : 0;
  });

  protected badgeFor(path: string): string | null {
    let count = 0;
    switch (path) {
      case '/tables':
        count = this.tablesCount();
        break;
      case '/billing':
        count = this.billingCount();
        break;
      case '/orders':
        count = this.ordersCount();
        break;
      case '/tasks':
        count = this.tasksCount();
        break;
    }
    return count > 0 ? (count > 99 ? '99+' : String(count)) : null;
  }

  /** "All changes saved" / "Saving N changes…" / "Offline" — from the offline replay queue. */
  protected readonly saveState = computed<{ tone: 'ok' | 'busy' | 'off'; label: string }>(() => {
    const pending = this.offlineQueue.pendingCount();
    if (this.connectivity.offline()) {
      return { tone: 'off', label: pending > 0 ? `Offline · ${pending} waiting` : 'Offline' };
    }
    if (pending > 0) return { tone: 'busy', label: `Saving ${pending} change${pending === 1 ? '' : 's'}…` };
    return { tone: 'ok', label: 'All changes saved' };
  });
}
